// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : image viewer low level image control
// =============================================================================
// ---Third-party imports
import React from 'react'
import clsx from 'clsx'
import {Draggable} from 'uix/chunks'

// ---Framework imports
import log from 'uix/logging'
import {
  useRef,
  useSelector,
  useSelectorShallow,
  useEffect,
  useActions,
  useMemo,
  useState,
  useResettableState,
  usePrevious,
} from 'uix/hooks'

// ---Local imports
import {setImageControl} from './actions'
import styles from './viewer.scss'

// usable as img/@src
const black64_96 =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNk+A8AAQUBAScY42YAAAAASUVORK5CYII='

// XXX: grow this a dname
// XXX: fix footers
export const wcUrl = (apiurl, wcSize, otid) =>
  `${apiurl}/download/webcache/${wcSize}/${otid}?orig=true`

export const ControlledImage = ({src, style, ...props}) => {
  let [imageLoaded, setImageLoaded] = useState(false) // useResettableState(false, [src])
  let [imageError, setImageError] = useResettableState(false, [src])
  let loaded = imageLoaded || imageError
  style = {
    ...style,
    opacity: loaded ? 1 : 0,
    transition: 'opacity 0.7s',
    backgroundColor: imageError ? '#FF00000F' : undefined,
  }
  return (
    <img
      {...props}
      src={src}
      style={style}
      onLoad={() => setImageLoaded(true)}
      onError={() => setImageError(true)}
    />
  )
}

export const ControlledImage2 = ({src, ...props}) => {
  /*
  -> initiated:
  -> queued:
  ->
  */

  let ref = useRef()
  let prevSrc = usePrevious(src)
  let prevImgctrl = useSelector(root => root.data.viewer.imageControl[prevSrc])

  let imgctrl = useSelector(root => root.data.viewer.imageControl[src])
  let actions = useActions({setImageControl})

  useEffect(() => {
    if (prevSrc && src !== prevSrc) {
      if (prevImgctrl === 'queued' || prevImgctrl === 'loading') {
        actions.setImageControl(prevSrc, 'aborted')
      }
    }
  }, [prevSrc, src, prevImgctrl])

  //~ let mounted = false

  useEffect(() => {
    if (!imgctrl || imgctrl === 'aborted') {
      actions.setImageControl(src, 'initiated')
    }
  }, [src, imgctrl])

  useEffect(() => {
    let img = ref.current
    if (img && imgctrl === 'queued') {
      //~ mounted = true

      let resolved = false
      let onLoad = () => {
        if (resolved) {
          return
        }
        //~ if (!mounted) return
        let isBroken = img.naturalWidth === 0
        //~ log('%o %o', imgctrl, src)
        actions.setImageControl(src, isBroken ? 'broken' : 'loaded')
        resolved = true
      }

      // in ssr img.complete can already be true
      if (false && img.complete) {
        log.warn('auto compl')
        onLoad()
      } else {
        actions.setImageControl(src, 'loading')
        img.onload = onLoad
        img.onerror = () => actions.setImageControl(src, 'broken')
      }
    }
  }, [src, ref, imgctrl])

  // on-unmount
  useEffect(
    () => () =>
      imgctrl === 'queued' || imgctrl === 'loading' || imgctrl === 'initiated'
        ? actions.setImageControl(src, 'aborted')
        : null,
    [src]
  )

  if (imgctrl === 'loading' || imgctrl === 'loaded' || imgctrl === 'aborted') {
    props.src = src
  } else if (imgctrl === 'broken') {
    props.src = black64_96
  } else if (prevSrc) {
    props.src = prevSrc
  } else {
    props._src = src
  }

  return <img className={clsx(styles.ctrlimg, imgctrl || 'initiated')} ref={ref} {...props} />
}

export const ControlledImageDebug = ({pages, onClick}) => {
  let {apiurl, imageControl, wcSizes} = useSelectorShallow(root => ({
    apiurl: root.config.apiurl,
    imageControl: root.data.viewer.imageControl,
    wcSizes: root.config.instance.webcache.sizes,
  }))

  let color = (otid, wcSize, orgWidth) => {
    let url = wcUrl(apiurl, wcSize, otid)
    if (wcSize > orgWidth) {
      return '#EEE'
    }
    switch (imageControl[url]) {
      case 'initiated':
        return 'blue'
      case 'queued':
        return 'blue'
      case 'loading':
        return 'yellow'
      case 'aborted':
        return 'khaki'
      case 'broken':
        return 'red'
      case 'loaded':
        return 'green'
      default:
        return ''
    }
  }
  let [pixels, setPixels] = useState(7)
  let pw = Math.max(4, pixels) + 3
  let items = useMemo(
    () =>
      pages.map(page => {
        let sizes = wcSizes.map(w => (
          <span
            key={w}
            title={`${page.id}/${w}`}
            onClick={() => onClick({otid: page.id, zoom: (w === 0 ? page.size[0] : w) - 1})}
            style={{
              display: 'inline-block',
              width: `${pw}px`,
              height: `${pixels}px`,
              backgroundColor: color(page.id, w, page.size[0]),
            }}
          >
            &nbsp;
          </span>
        ))
        return (
          <div
            key={page.id}
            style={{height: `${pixels}px`, backgroundColor: 'white', cursor: 'default'}}
          >
            {sizes}
          </div>
        )
      }),
    [pages, imageControl, pixels]
  )

  return (
    <Draggable handle=".handle">
      <div
        className="handle"
        onWheel={e => setPixels(Math.max(2, pixels) - Math.sign(e.deltaY))}
        style={{
          zIndex: 1000,
          border: '1px solid #EEE',
          position: 'absolute',
          top: '2px',
          left: '2%',
          width: `${wcSizes.length * pw + 2}px`,
        }}
      >
        {items}
      </div>
    </Draggable>
  )
}
